import classnames from 'classnames';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'palette/Button';
import { Drawer } from 'palette/Drawer';
import { FilterMobileActions } from 'palette/Filters';
import {
    ModalBoxBody, ModalBoxFooter, ModalBoxHeader, useModal
} from 'palette/Modal';
import { SearchInput } from 'palette/SearchInput';
import { applyCurrentFilters, deselectAllFilters, removeFiltersByType } from '@common/Filters/actions/filtersActions';
import { FiltersTypes } from '@common/Filters/containers/FiltersTypes';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { selectListingPageInfo } from '@common/Listing/selectors/ListingSelector';
import { SUB_CAT_HORIZONTAL_MODAL_ID } from '@common/SubCategories/constants';
import { GA_FILTER_CLEAR_ACTION, GA_FILTER_CLEAR_ALL_LABEL, GA_FILTERS_CATEGORY } from '@common/Tracking';
import { pushGAEventToDataLayer } from '@helpers/dataLayer';
import { isStoreBoozt, isWebshopMobile } from '@helpers/env';
import { FILTERS_DRAWER_ID, SORT_BY_DRAWER_ID } from '../constants';
import { resetViewAll } from '../helpers/resetViewAll';
import { Filters } from './Filters';
import { FiltersPanelClearFilterBtn } from './FiltersPanelClearFilterBtn';
import SortingList from './SortingList';
import './FiltersPanel.scss';

export function FiltersPanel({ hasSubCategories, onModalClose }) {
    const dispatch = useDispatch();
    const translate = useTranslation();
    const config = useConfig();
    const { setActiveModalId } = useModal();
    const [currentFilterType, setCurrentFilterType] = useState(null);
    const [search, setSearch] = useState('');

    const sortBy = useSelector(({ sortBy: sortByFromState }) => sortByFromState);
    const { brand: currentBrand } = useSelector(selectListingPageInfo);
    const isMobileDevice = isWebshopMobile(config);
    const isBoozt = isStoreBoozt(config);
    const hasMobileInlineFiltersTest = !isBoozt && isMobileDevice;
    const { activeFilters } = useSelector(state => state.filters);

    const currentSorting = find(sortBy, ({ selected }) => selected);

    useEffect(() => {
        document.addEventListener('slidepanel:filters:close', resetViewAll);

        return () => {
            document.removeEventListener('slidepanel:filters:close', resetViewAll);
        };
    }, []);

    const closeModals = () => setActiveModalId(null);
    const openSortByModal = () => setActiveModalId(SORT_BY_DRAWER_ID);
    const openFiltersModal = () => setActiveModalId(FILTERS_DRAWER_ID);
    const openCategoriesModal = () => setActiveModalId(SUB_CAT_HORIZONTAL_MODAL_ID);
    const showBackButton = !!currentFilterType;

    const handleAllFiltersClick = () => {
        setCurrentFilterType(null);
        openFiltersModal();
    };

    const handleSingleFilterClick = (filterType) => {
        setCurrentFilterType(filterType);
        openFiltersModal();
    };

    const labels = {
        sorting: currentSorting && Number(currentSorting.value) !== -1
            ? currentSorting.label : translate('listing.filter.sort_by.label'),
        filters: translate('filters'),
        categories: translate('listing.subcategories.cbutton')
    };

    const handleClearFilter = () => {
        pushGAEventToDataLayer(GA_FILTERS_CATEGORY, currentFilterType || GA_FILTER_CLEAR_ACTION, GA_FILTER_CLEAR_ALL_LABEL);

        if (currentFilterType) {
            dispatch(removeFiltersByType(currentFilterType));
            dispatch(applyCurrentFilters());
        } else {
            dispatch(deselectAllFilters());
        }
    };

    const getTitle = () => {
        let title = translate('listing.filter.by.label');

        if (currentFilterType) {
            const key = `listing.filter.${currentFilterType}.label`.replace('tag-', '');
            title += ` ${translate(key)}`;
        }

        return title;
    };

    const closeFilterAndSortModal = () => {
        closeModals();

        setTimeout(() => {
            onModalClose?.();
        }, 1);
    };

    const slidePanelBackCallback = () => {
        setCurrentFilterType(null);
    };

    const delayedSearch = debounce(value => setSearch(value), 100);

    const handleSearch = (event) => {
        event.persist();
        const searchValue = event.target.value;
        delayedSearch(searchValue);
    };

    const isSearch = ['brand', 'tag-productseries'].includes(currentFilterType);

    return (
        <div className={classnames('filters-panel')}>
            {
                isMobileDevice && (
                    <FilterMobileActions
                        labels={labels}
                        onFiltersClick={handleAllFiltersClick}
                        onSortClick={openSortByModal}
                        {...isBoozt && hasSubCategories && {
                            id: 'mobile-filters-panel-subcategories-test',
                            onCategoryClick: openCategoriesModal
                        }}
                        count={activeFilters?.length}
                    />
                )
            }
            {
                hasMobileInlineFiltersTest && (
                    <FiltersTypes
                        isInline
                        id="mobile-inline-filters-b-test"
                        setCurrentFilterType={handleSingleFilterClick}
                    />
                )
            }
            <Drawer
                anchor="right"
                id={FILTERS_DRAWER_ID}
                onClose={closeModals}
            >
                <ModalBoxHeader
                    leftActionIcon={showBackButton ? 'palette_chevron_left' : undefined}
                    title={getTitle()}
                    titleAlign="center"
                    onLeftActionClick={showBackButton ? slidePanelBackCallback : undefined}
                />
                <ModalBoxBody>
                    {isSearch
                        && (
                            <SearchInput
                                className="filters-panel__search"
                                inputClassName="skip-generic-styling"
                                placeholder={translate('brandwall.search.placeholder')}
                                value={search}
                                onChange={handleSearch}
                            />
                        )}
                    <Filters
                        activeBrandId={currentBrand}
                        currentFilterType={currentFilterType}
                        search={search}
                        theme="mod2"
                        onFilterTypeUpdate={(type) => { setCurrentFilterType(type); }}
                    />
                </ModalBoxBody>
                <ModalBoxFooter>
                    <div className="filters-panel__footer">
                        <FiltersPanelClearFilterBtn
                            currentFilterType={currentFilterType}
                            onClick={handleClearFilter}
                        />
                        <Button
                            isExpanded
                            label={translate('listing.filter.view_selection.label')}
                            onClick={closeFilterAndSortModal}
                        />
                    </div>
                </ModalBoxFooter>
            </Drawer>
            <Drawer
                rounded
                anchor="bottom"
                id={SORT_BY_DRAWER_ID}
                onClose={closeModals}
            >
                <ModalBoxHeader
                    title={translate('listing.sorting.label')}
                    titleAlign="center"
                />
                <ModalBoxBody>
                    <SortingList
                        closeSlidePanel={closeFilterAndSortModal}
                        theme="mod1"
                    />
                </ModalBoxBody>
            </Drawer>
        </div>
    );
}
